
import Button from '@components/designSystem/Button'
import ProductDetailsCard, { Block } from '@components/productDetails/ProductDetailsCard'
import useCanWriteTasks from '@hooks/useCanWriteTasks'
import useTypedTranslation from '@hooks/useTypedTranslation'
import { useTodoItemByGtin, useCompleteTask, useReopenTask } from '@state/todoList'

import type { TodoItemWrapper } from '@state/util/todoList'

interface TaskProductDetailsCardProps {
  selectedGtin?: string
}

interface CompleteTaskButtonProps {
  todoItem: TodoItemWrapper
}

function CompleteTaskButton({ todoItem }: CompleteTaskButtonProps) {
  const { t } = useTypedTranslation()
  const { mutate: completeTask } = useCompleteTask()
  const { mutate: reopenTask } = useReopenTask()
  const canWriteTasks = useCanWriteTasks()

  async function completeItem() {
    completeTask({ taskId: todoItem.id })
  }

  async function reopenItem() {
    reopenTask({ taskId: todoItem.id })
  }

  if (!canWriteTasks) return null

  let buttonText = ''
  if (todoItem.isCompleted()) {
    buttonText = t('todoList.undo')
  } else {
    buttonText = todoItem.action === 'CHECK' ? t('todoList.badges.CHECKED') :
      todoItem.action === 'REFILL' ? t('todoList.badges.REFILLED') :
        ''
  }

  return (
    <Button
      variant={todoItem.isCompleted() ? 'outline' : 'filled'}
      className='mx-4'
      onClick={todoItem.isCompleted() ? reopenItem : completeItem}
    >
      {buttonText}
    </Button>
  )
}

function TaskProductDetailsCard({ selectedGtin }: TaskProductDetailsCardProps) {
  const { t } = useTypedTranslation()
  const todoItem = useTodoItemByGtin(selectedGtin)

  if (!todoItem) return <ProductDetailsCard />

  const taskTitle = todoItem.action === 'CHECK' ? t('todoList.badges.CHECK') :
    todoItem.action === 'REFILL' ? t('todoList.badges.REFILL') :
      ''

  return (
    <ProductDetailsCard
      selectedGtin={selectedGtin}
      additionalBlocks={[
        <Block label={t('todoList.task')} value={taskTitle} />,
      ].filter(Boolean)}
      utilities={[
        <CompleteTaskButton todoItem={todoItem} />,
      ]}
    />
  )
}

export default TaskProductDetailsCard